import { useState, useEffect } from 'react';
import { aiContentAPI, featureFlagsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Sparkles,
  Loader2,
  Copy,
  Check,
  History,
  Wand2,
  Shield,
  RefreshCw,
  FileText,
  MessageSquare,
  Image,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import FeatureGuard from '../components/FeatureGuard';

const contentTypes = [
  { value: 'post', label: 'Bài viết', icon: FileText },
  { value: 'comment', label: 'Bình luận', icon: MessageSquare },
  { value: 'caption', label: 'Caption ảnh', icon: Image },
];

const tones = [
  { value: 'friendly', label: 'Thân thiện' },
  { value: 'professional', label: 'Chuyên nghiệp' },
  { value: 'funny', label: 'Hài hước' },
  { value: 'serious', label: 'Nghiêm túc' },
  { value: 'emotional', label: 'Cảm xúc' },
];

export default function AIContent() {
  const { user, isAdmin, refreshUser } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [contentType, setContentType] = useState('post');
  const [tone, setTone] = useState('friendly');
  const [applyBypass, setApplyBypass] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [featureFlag, setFeatureFlag] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const [historyRes, flagsRes] = await Promise.all([
        aiContentAPI.getHistory(),
        featureFlagsAPI.getAll()
      ]);
      setHistory(historyRes.data);
      const flag = flagsRes.data.find(f => f.id === 'ai_content');
      setFeatureFlag(flag);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Vui lòng nhập yêu cầu content');
      return;
    }

    setGenerating(true);
    setResult(null);
    
    try {
      const res = await aiContentAPI.generate(prompt, contentType, tone, applyBypass);
      setResult(res.data);
      await refreshUser();
      await fetchHistory();
      toast.success('Tạo content thành công!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tạo content thất bại');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success('Đã copy!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error('Copy thất bại');
    }
  };

  const handleUseFromHistory = (item) => {
    setPrompt(item.prompt);
    setContentType(item.content_type);
    setTone(item.tone);
    setApplyBypass(item.applied_bypass);
    setResult({
      original_content: item.original_content,
      bypassed_content: item.bypassed_content,
      applied_bypass: item.applied_bypass
    });
  };

  return (
    <FeatureGuard feature={featureFlag}>
      <div className="space-y-6" data-testid="ai-content-page">
      {/* Header */}
      <div className="animate-fadeIn">
          <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
            <Sparkles className="text-[#1877F2]" />
            Viết content AI
            {featureFlag?.is_vip && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] uppercase font-bold rounded-full border border-amber-200 flex items-center gap-1">
                <Crown size={12} />
                VIP
              </span>
            )}
          </h1>
          <p className="text-[#606770]">
            Sáng tạo nội dung thu hút với sự hỗ trợ của trí tuệ nhân tạo (GPT/Claude)
          </p>
      </div>

      <Tabs defaultValue="create" className="animate-fadeIn stagger-1">
        <TabsList className="bg-[#F0F2F5] p-1">
          <TabsTrigger value="create" className="data-[state=active]:bg-white" data-testid="create-tab">
            <Wand2 size={16} className="mr-2" />
            Tạo content
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white" data-testid="history-tab">
            <History size={16} className="mr-2" />
            Lịch sử ({history.length})
          </TabsTrigger>
        </TabsList>

        {/* Create Tab */}
        <TabsContent value="create" className="mt-6 space-y-6">
          {/* Input Card */}
          <Card data-testid="create-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Tạo content mới</CardTitle>
              <CardDescription>
                Mô tả content bạn muốn tạo, AI sẽ viết cho bạn {featureFlag?.is_vip ? '' : (featureFlag?.credit_cost > 0 ? `(${featureFlag.credit_cost} credits/lần)` : '(Miễn phí)')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Content Type & Tone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Loại content</Label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger data-testid="content-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon size={16} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Phong cách</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger data-testid="tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tones.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Prompt */}
              <div className="space-y-2">
                <Label htmlFor="prompt">Yêu cầu / Mô tả content</Label>
                <Textarea
                  id="prompt"
                  placeholder="VD: Viết bài về sản phẩm skincare mới, tone hài hước, nhắm đến phụ nữ 25-35 tuổi..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  data-testid="prompt-input"
                />
              </div>

              {/* Bypass Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#FEF9E6] border border-[#FFE69C] rounded-xl">
                <div className="flex items-center gap-3">
                  <Shield size={24} className="text-[#8F6702]" />
                  <div>
                    <Label className="text-base font-semibold text-[#1C1E21]">Lách chính sách</Label>
                    <p className="text-sm text-[#606770]">
                      Thay thế ký tự để tránh bị Facebook chặn
                    </p>
                  </div>
                </div>
                <Switch
                  checked={applyBypass}
                  onCheckedChange={setApplyBypass}
                  data-testid="bypass-switch"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim() || (featureFlag?.is_vip && user?.subscription_type !== 'monthly')}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5] h-12 text-base font-semibold"
                data-testid="generate-button"
              >
                {generating ? (
                  <><Loader2 size={20} className="mr-2 animate-spin" />Đang sáng tạo...</>
                ) : (
                  <><Sparkles size={20} className="mr-2" />
                  {!isAdmin && featureFlag?.is_vip && user?.subscription_type !== 'monthly' ? (
                    "Dành cho VIP"
                  ) : (
                    featureFlag?.is_vip ? "Tạo nội dung (VIP)" : (featureFlag?.credit_cost > 0 ? `Tạo nội dung (${featureFlag.credit_cost} credits)` : "Tạo nội dung (Miễn phí)")
                  )}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Result Card */}
          {result && (
            <Card className="border-2 border-[#31A24C] animate-fadeIn" data-testid="result-card">
              <CardHeader className="bg-[#EBF5ED]">
                <CardTitle className="text-lg font-semibold flex items-center gap-2 text-[#1E7132]">
                  <Check size={20} />
                  Content đã tạo
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Original Content */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Content gốc</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(result.original_content, 'original')}
                      data-testid="copy-original-btn"
                    >
                      {copied === 'original' ? <Check size={14} /> : <Copy size={14} />}
                      <span className="ml-1">Copy</span>
                    </Button>
                  </div>
                  <div className="p-4 bg-[#F7F8FA] rounded-xl whitespace-pre-wrap text-[#1C1E21]">
                    {result.original_content}
                  </div>
                </div>

                {/* Bypassed Content */}
                {result.applied_bypass && result.bypassed_content && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <Shield size={16} className="text-[#8F6702]" />
                        Content đã lách
                      </Label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopy(result.bypassed_content, 'bypassed')}
                        className="border-[#8F6702] text-[#8F6702]"
                        data-testid="copy-bypassed-btn"
                      >
                        {copied === 'bypassed' ? <Check size={14} /> : <Copy size={14} />}
                        <span className="ml-1">Copy</span>
                      </Button>
                    </div>
                    <div className="p-4 bg-[#FEF9E6] border border-[#FFE69C] rounded-xl whitespace-pre-wrap text-[#1C1E21]">
                      {result.bypassed_content}
                    </div>
                    <p className="text-xs text-[#8C939D]">
                      * Các ký tự đã được thay thế bằng ký tự Unicode tương tự để tránh bị phát hiện
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card data-testid="history-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Lịch sử tạo content</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-[#1877F2]" />
                </div>
              ) : history.length > 0 ? (
                <div className="space-y-4">
                  {history.map((item) => (
                    <div 
                      key={item.id}
                      className="p-4 bg-[#F7F8FA] rounded-xl hover:bg-[#F0F2F5] transition-colors"
                      data-testid={`history-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-[#E7F3FF] text-[#1877F2] text-xs font-semibold rounded">
                              {contentTypes.find(t => t.value === item.content_type)?.label || item.content_type}
                            </span>
                            <span className="px-2 py-0.5 bg-[#F0F2F5] text-[#606770] text-xs rounded">
                              {tones.find(t => t.value === item.tone)?.label || item.tone}
                            </span>
                            {item.applied_bypass && (
                              <span className="px-2 py-0.5 bg-[#FEF9E6] text-[#8F6702] text-xs font-semibold rounded flex items-center gap-1">
                                <Shield size={10} />
                                Đã lách
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#606770] mb-2 line-clamp-2">
                            <strong>Yêu cầu:</strong> {item.prompt}
                          </p>
                          <p className="text-sm text-[#1C1E21] line-clamp-3">
                            {item.original_content}
                          </p>
                          <p className="text-xs text-[#8C939D] mt-2">
                            {new Date(item.created_at).toLocaleString('vi-VN')}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCopy(item.applied_bypass ? item.bypassed_content : item.original_content, item.id)}
                            data-testid={`copy-history-${item.id}`}
                          >
                            {copied === item.id ? <Check size={14} /> : <Copy size={14} />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUseFromHistory(item)}
                            data-testid={`use-history-${item.id}`}
                          >
                            <RefreshCw size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <History size={48} className="empty-state-icon mx-auto" />
                  <h3 className="empty-state-title">Chưa có lịch sử</h3>
                  <p className="empty-state-text">
                    Tạo content đầu tiên để xem lịch sử ở đây
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </FeatureGuard>
  );
}
